import { useCallback, useEffect, useState } from "react";
import { ArrowClockwise, CreditCard, MagnifyingGlass, UserCircle, WarningCircle } from "@phosphor-icons/react";
import { Button, IconButton, TextField } from "@radix-ui/themes";

import { Pagination } from "../../components/Pagination";
import { useAdminRead } from "../../hooks/useAdminRead";
import { useCursorDirectory } from "../../hooks/useCursorDirectory";
import { readAdminData } from "../../services/adminApi";

import type { AccountDetail, AccountDirectoryData } from "../../types";

const formatDate = (value: number | null): string => value
  ? new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" }).format(value)
  : "—";

export function Accounts() {
  const [query, setQuery] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const loadDirectory = useCallback((cursor?: string) => readAdminData<AccountDirectoryData>(
    cursor
      ? { view: "accountDirectory", pageSize: 20, cursor }
      : { view: "accountDirectory", pageSize: 20 },
  ), []);
  const directory = useCursorDirectory({ load: loadDirectory });
  const loadDetail = useCallback(() => readAdminData<AccountDetail>({ view: "account", query: committedQuery }), [committedQuery]);
  const { data, error, isLoading, refresh, reset } = useAdminRead({ load: loadDetail, loadOnMount: false });

  useEffect(() => { if (committedQuery) void refresh(); }, [committedQuery, refresh]);

  const openAccount = (accountId: string) => {
    setQuery(accountId);
    if (accountId === committedQuery) void refresh();
    else {
      reset();
      setCommittedQuery(accountId);
    }
  };

  const search = () => {
    const normalized = query.trim();
    if (normalized) openAccount(normalized);
  };

  const records = directory.data?.records ?? [];

  return (
    <main className="page">
      <section className="page-heading"><div><span className="breadcrumb">HESAPLAR / DESTEK</span><h1>Hesaplar</h1><p>Mevcut hesapları sayfalar halinde görüntüleyin veya tam Firebase UID ve e-posta ile doğrudan açın.</p></div></section>
      <section className="search-panel">
        <label><span>UID veya e-posta</span><div className="search-control"><MagnifyingGlass size={18} /><TextField.Root value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") search(); }} placeholder="user@example.com veya Firebase UID" /><Button disabled={!query.trim() || isLoading} onClick={search} type="button">{isLoading ? "Aranıyor…" : "Hesabı aç"}</Button></div></label>
      </section>

      <section className="panel directory-panel">
        <div className="panel-heading directory-heading">
          <div><span className="kicker">HESAP DİZİNİ</span><h2>Mevcut hesaplar</h2><p>Sayfa {directory.currentPage + 1} · {records.length} kayıt</p></div>
          <IconButton aria-label="Hesap listesini yenile" disabled={directory.isLoading} onClick={() => void directory.refresh()} type="button" variant="soft"><ArrowClockwise size={18} /></IconButton>
        </div>
        {directory.error ? <div className="alert danger"><WarningCircle size={21} /><span><strong>Hesap listesi yüklenemedi</strong>{directory.error}</span></div> : null}
        <div className="directory-scroll">
          <table className="directory-table account-directory-table">
            <thead><tr><th>Hesap</th><th>Durum</th><th>Doğrulama</th><th>Son giriş</th><th><span className="sr-only">İşlem</span></th></tr></thead>
            <tbody>
              {records.map((account) => <tr key={account.id}>
                <td><strong>{account.displayName ?? account.email ?? "İsimsiz hesap"}</strong><small>{account.email ?? account.id}</small>{account.email ? <code>{account.id}</code> : null}</td>
                <td><span className={`directory-status ${account.disabled ? "danger" : "healthy"}`}><i />{account.disabled ? "Devre dışı" : "Aktif"}</span></td>
                <td>{account.emailVerified ? "Doğrulandı" : "Bekliyor"}</td>
                <td>{formatDate(account.lastSignInAt)}</td>
                <td><Button onClick={() => openAccount(account.id)} size="1" type="button" variant="soft">Ayrıntıyı aç</Button></td>
              </tr>)}
            </tbody>
          </table>
        </div>
        {!directory.isLoading && !directory.error && records.length === 0 ? <p className="directory-empty">Gösterilecek hesap bulunamadı.</p> : null}
        {directory.isLoading && !directory.data ? <p className="directory-empty">Hesaplar yükleniyor…</p> : null}
        <Pagination currentPage={directory.currentPage} hasNextPage={directory.hasNextPage} isLoading={directory.isLoading} knownPageCount={directory.knownPageCount} onPageChange={(page) => void directory.loadPage(page)} />
      </section>

      {error ? <div className="alert danger"><WarningCircle size={21} /><span><strong>Hesap yüklenemedi</strong>{error}</span></div> : null}
      {data ? (
        <>
          <section className="detail-hero">
            <div className="avatar"><UserCircle size={30} /></div>
            <div><span className="kicker">HESAP</span><h2>{data.email ?? "E-posta bulunamadı"}</h2><code>{data.id}</code></div>
            <span className={`pill ${data.status}`}>{data.status}</span>
          </section>
          <section className="detail-grid">
            <article className="panel"><div className="panel-heading"><div><span className="kicker">KİMLİK</span><h2>Hesap ayrıntıları</h2></div></div><dl className="detail-list"><div><dt>Plan</dt><dd>{data.planId}</dd></div><div><dt>Plan bitişi</dt><dd>{formatDate(data.planExpiresAt)}</dd></div><div><dt>Oluşturulma</dt><dd>{formatDate(data.createdAt)}</dd></div><div><dt>Son giriş</dt><dd>{formatDate(data.lastSignInAt)}</dd></div><div><dt>Billing revision</dt><dd>{data.billingRevision ?? "—"}</dd></div></dl></article>
            <article className="panel"><div className="panel-heading"><div><span className="kicker">MAĞAZA</span><h2>Paket kaydı</h2></div><CreditCard size={21} /></div>{data.subscription ? <dl className="detail-list"><div><dt>Sağlayıcı</dt><dd>{data.subscription.provider}</dd></div><div><dt>Ortam</dt><dd>{data.subscription.environment}</dd></div><div><dt>Durum</dt><dd>{data.subscription.state}</dd></div><div><dt>Bitiş</dt><dd>{formatDate(data.subscription.expiresAt)}</dd></div></dl> : <p className="muted-copy">Bağlı mağaza kaydı bulunmuyor.</p>}</article>
          </section>
          <section className="panel"><div className="panel-heading"><div><span className="kicker">WORKSPACE ÜYELİKLERİ</span><h2>Bağlı alanlar</h2></div><span className="count-label">{data.workspaces.length}{data.hasMoreWorkspaces ? "+" : ""} kayıt</span></div><div className="data-table"><div className="data-row data-head"><span>Workspace</span><span>Tür</span><span>Rol</span><span>Durum</span></div>{data.workspaces.map((workspace) => <div className="data-row" key={workspace.id}><span><strong>{workspace.name ?? workspace.id}</strong><small>{workspace.id}</small></span><span>{workspace.kind}</span><span>{workspace.role}</span><span><i className={`status-dot status-${workspace.status === "active" ? "healthy" : "attention"}`} />{workspace.status}</span></div>)}</div></section>
        </>
      ) : null}
    </main>
  );
}
